import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import type { SearchResultScope as SearchResultScopeData } from '../../types';

interface SearchResultScopeProps {
  scope: SearchResultScopeData;
  displayedCount: number;
}

const entityNouns = (
  entity: SearchResultScopeData['entity']
): { singular: string; plural: string } => {
  if (entity === 'application') {
    return { singular: 'application', plural: 'applications' };
  }
  if (entity === 'candidate') {
    return { singular: 'candidate', plural: 'candidates' };
  }
  return { singular: 'contact', plural: 'contacts' };
};

export const SearchResultScope = ({
  scope,
  displayedCount,
}: SearchResultScopeProps) => {
  const nouns = entityNouns(scope.entity);
  const matchingCount = scope.matchingCount;
  const noun = matchingCount === 1 ? nouns.singular : nouns.plural;
  const headline =
    typeof matchingCount === 'number'
      ? `${matchingCount.toLocaleString()} ${noun} in this filtered result set`
      : `Filtered ${nouns.plural}`;

  let displayedDetail = '';
  if (typeof matchingCount === 'number' && displayedCount < matchingCount) {
    displayedDetail = ` Showing the top ${displayedCount.toLocaleString()} ranked ${
      displayedCount === 1 ? 'result' : 'results'
    }.`;
  } else if (
    typeof matchingCount === 'number' &&
    displayedCount > matchingCount
  ) {
    const alternatives = displayedCount - matchingCount;
    displayedDetail = ` The list also shows ${alternatives.toLocaleString()} close ${
      alternatives === 1 ? 'alternative' : 'alternatives'
    }.`;
  }
  const exactDetail =
    typeof scope.exactMatchingCount === 'number' &&
    scope.exactMatchingCount !== matchingCount
      ? ` ${scope.exactMatchingCount.toLocaleString()} ${
          scope.exactMatchingCount === 1
            ? 'result satisfies'
            : 'results satisfy'
        } every exact filter.`
      : '';
  const clippedQuery =
    scope.query && scope.query.length > 160
      ? `${scope.query.slice(0, 157)}…`
      : scope.query;
  const scopeDetail =
    scope.workspaceTotalKnown === true
      ? 'Count for the current search criteria and filters.'
      : 'Search-result count — based on the current criteria and filters, not your workspace total.';

  return (
    <Box
      px="10px"
      py="8px"
      borderRadius="8px"
      bg="secondary-50"
      data-test-id="agentforge-search-result-scope"
    >
      <Text variant="small" as="p" className="font-medium text-secondary-900">
        {headline}
      </Text>
      <Text variant="caption" as="p" className="text-secondary-600">
        {scopeDetail}
        {exactDetail}
        {displayedDetail}
      </Text>
      {clippedQuery && (
        <Text
          variant="caption"
          as="p"
          className="text-secondary-600"
          data-test-id="agentforge-search-result-query"
        >
          Search: “{clippedQuery}”
        </Text>
      )}
      {scope.appliedFilters && scope.appliedFilters.length > 0 && (
        <Flex wrap="wrap" gap="6px" mt="6px">
          {scope.appliedFilters.map((filter) => (
            <Box
              key={filter}
              px="8px"
              py="2px"
              borderRadius="6px"
              bg="base-white"
              color="secondary-700"
              fontSize="12px"
              fontWeight="500"
            >
              {filter}
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
};
